// ===== scheduleSlice.js =====
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../api/axiosInstance';

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/students/schedules/`;
const DRAFT_SCHEDULES_URL = `${process.env.REACT_APP_API_BASE_URL}/api/students/draft-schedules/`;
const PERIOD_TIMINGS_URL = `${process.env.REACT_APP_API_BASE_URL}/api/students/period-timings/`;
const SCHEDULE_CONFIGS_URL = `${process.env.REACT_APP_API_BASE_URL}/api/students/schedule-configs/`;

// ===== Weekly Schedule Actions =====

// Fetch all schedules
export const fetchSchedules = createAsyncThunk(
  'schedule/fetchSchedules', 
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.school_class) queryParams.append('school_class', params.school_class);
      if (params.day_of_week) queryParams.append('day_of_week', params.day_of_week);
      if (params.slot_id) queryParams.append('slot_id', params.slot_id);
      if (params.teacher) queryParams.append('teacher', params.teacher);
      if (params.subject) queryParams.append('subject', params.subject);
      if (params.slot_type) queryParams.append('slot_type', params.slot_type);
      if (params.ordering) queryParams.append('ordering', params.ordering);
      
      const url = queryParams.toString() ? `${BASE_URL}?${queryParams}` : BASE_URL;
      const res = await axios.get(url);
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Fetch enhanced schedules (with additional metadata)
export const fetchEnhancedSchedules = createAsyncThunk('schedule/fetchEnhancedSchedules', async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  Object.keys(params).forEach(key => {
    if (params[key]) queryParams.append(key, params[key]);
  });
  
  const url = `${BASE_URL}enhanced/${queryParams.toString() ? `?${queryParams}` : ''}`;
  const res = await axios.get(url);
  return res.data;
});

// Fetch schedule for specific class
export const fetchClassSchedule = createAsyncThunk(
  'schedule/fetchClassSchedule', 
  async (classId, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/students/classes/${classId}/schedule/`);
      return { classId, schedules: res.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Fetch schedule for specific teacher
export const fetchTeacherSchedule = createAsyncThunk('schedule/fetchTeacherSchedule', async (teacherId) => {
  const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/students/teachers/${teacherId}/schedule/`);
  return { teacherId, schedules: res.data };
});

// Fetch schedules for specific subject
export const fetchSchedulesBySubject = createAsyncThunk(
  'schedule/fetchSchedulesBySubject',
  async (subjectId, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE_URL}?subject=${subjectId}`);
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Get specific schedule by ID
export const fetchScheduleById = createAsyncThunk('schedule/fetchScheduleById', async (id) => {
  const res = await axios.get(`${BASE_URL}${id}/`);
  return res.data;
});

// Create new schedule entry
export const createSchedule = createAsyncThunk(
  'schedule/createSchedule', 
  async (scheduleData, { rejectWithValue }) => {
    try {
      const res = await axios.post(BASE_URL, scheduleData);
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Bulk create schedule entries
export const bulkCreateSchedules = createAsyncThunk(
  'schedule/bulkCreateSchedules', 
  async (schedulesData, { rejectWithValue }) => {
    try {
      console.log('🚀 Redux action called with:', schedulesData);
      const res = await axios.post(`${BASE_URL}bulk-create/`, schedulesData);
      console.log('✅ Redux action success:', res.data);
      return res.data;
    } catch (error) {
      console.error('❌ Redux action error:', error);
      console.error('❌ Error response:', error.response?.data);
      
      // Return the actual server response data, not just the error message
      if (error.response?.data) {
        return rejectWithValue(error.response.data);
      }
      
      // Fallback for network errors or other issues
      return rejectWithValue({
        message: error.message || 'حدث خطأ في الشبكة',
        status: error.response?.status || 0
      });
    }
  }
);

// Update schedule entry
export const updateSchedule = createAsyncThunk(
  'schedule/updateSchedule', 
  async ({ id, ...scheduleData }, { rejectWithValue }) => {
    try {
      const res = await axios.patch(`${BASE_URL}${id}/`, scheduleData);
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Delete schedule entry
export const deleteSchedule = createAsyncThunk('schedule/deleteSchedule', async (id) => {
  await axios.delete(`${BASE_URL}${id}/`);
  return id;
});

// ===== Draft Schedule Actions =====

// Fetch all draft schedules
export const fetchDraftSchedules = createAsyncThunk(
  'schedule/fetchDraftSchedules',
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.school_class) queryParams.append('school_class', params.school_class);
      if (params.day_of_week) queryParams.append('day_of_week', params.day_of_week);
      if (params.teacher) queryParams.append('teacher', params.teacher);
      
      const url = queryParams.toString() ? `${DRAFT_SCHEDULES_URL}?${queryParams}` : DRAFT_SCHEDULES_URL;
      const res = await axios.get(url);
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Bulk create/update/delete draft schedules
export const bulkCreateDraftSchedules = createAsyncThunk(
  'schedule/bulkCreateDraftSchedules',
  async (data, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${DRAFT_SCHEDULES_URL}bulk-create/`, data);
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Delete draft schedule
export const deleteDraftSchedule = createAsyncThunk('schedule/deleteDraftSchedule', async (id) => {
  await axios.delete(`${DRAFT_SCHEDULES_URL}${id}/`);
  return id;
});

// Publish draft schedules to permanent schedules
export const publishDraftSchedules = createAsyncThunk(
  'schedule/publishDraftSchedules',
  async (data, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${DRAFT_SCHEDULES_URL}publish/`, data);
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  }
);

// Check for schedule conflicts
export const checkScheduleConflicts = createAsyncThunk('schedule/checkConflicts', async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.class_id) queryParams.append('class_id', params.class_id);
  if (params.teacher_id) queryParams.append('teacher_id', params.teacher_id);
  if (params.day_of_week) queryParams.append('day_of_week', params.day_of_week);
  
  const url = `${BASE_URL}conflicts/${queryParams.toString() ? `?${queryParams}` : ''}`;
  const res = await axios.get(url);
  return res.data;
});

// Get schedule analytics for a class
export const fetchScheduleAnalytics = createAsyncThunk('schedule/fetchAnalytics', async (classId) => {
  const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/students/classes/${classId}/schedule/analytics/`);
  return { classId, analytics: res.data };
});

// Get schedule recommendations for a class
export const fetchScheduleRecommendations = createAsyncThunk('schedule/fetchRecommendations', async (classId) => {
  const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/students/classes/${classId}/schedule/recommendations/`);
  return { classId, recommendations: res.data };
});

// ===== Period Timings Actions =====

// Fetch period timings
export const fetchPeriodTimings = createAsyncThunk('schedule/fetchPeriodTimings', async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
  if (params.ordering) queryParams.append('ordering', params.ordering);
  
  const url = queryParams.toString() ? `${PERIOD_TIMINGS_URL}?${queryParams}` : PERIOD_TIMINGS_URL;
  const res = await axios.get(url);
  return res.data;
});

// Get specific period timing by ID
export const fetchPeriodTimingById = createAsyncThunk('schedule/fetchPeriodTimingById', async (id) => {
  const res = await axios.get(`${PERIOD_TIMINGS_URL}${id}/`);
  return res.data;
});

// Create period timing
export const createPeriodTiming = createAsyncThunk('schedule/createPeriodTiming', async (timingData) => {
  const res = await axios.post(PERIOD_TIMINGS_URL, timingData);
  return res.data;
});

// Update period timing
export const updatePeriodTiming = createAsyncThunk('schedule/updatePeriodTiming', async ({ id, ...timingData }) => {
  const res = await axios.patch(`${PERIOD_TIMINGS_URL}${id}/`, timingData);
  return res.data;
});

// Delete period timing
export const deletePeriodTiming = createAsyncThunk('schedule/deletePeriodTiming', async (id) => {
  await axios.delete(`${PERIOD_TIMINGS_URL}${id}/`);
  return id;
});

// ===== Schedule Configuration Actions =====

// Fetch schedule configurations
export const fetchScheduleConfigurations = createAsyncThunk('schedule/fetchScheduleConfigurations', async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.is_default !== undefined) queryParams.append('is_default', params.is_default);
  if (params.school_class) queryParams.append('school_class', params.school_class);
  if (params.ordering) queryParams.append('ordering', params.ordering);
  
  const url = queryParams.toString() ? `${SCHEDULE_CONFIGS_URL}?${queryParams}` : SCHEDULE_CONFIGS_URL;
  const res = await axios.get(url);
  return res.data;
});

// Create schedule configuration
export const createScheduleConfiguration = createAsyncThunk('schedule/createScheduleConfiguration', async (configData) => {
  const res = await axios.post(SCHEDULE_CONFIGS_URL, configData);
  return res.data;
});

// Update schedule configuration
export const updateScheduleConfiguration = createAsyncThunk('schedule/updateScheduleConfiguration', async ({ id, ...configData }) => {
  const res = await axios.patch(`${SCHEDULE_CONFIGS_URL}${id}/`, configData);
  return res.data;
});

// Delete schedule configuration
export const deleteScheduleConfiguration = createAsyncThunk('schedule/deleteScheduleConfiguration', async (id) => {
  await axios.delete(`${SCHEDULE_CONFIGS_URL}${id}/`);
  return id;
});

// Auto-fill schedule for a class
export const autoFillSchedule = createAsyncThunk('schedule/autoFillSchedule', async ({ classId, preferences = {} }) => {
  const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/students/classes/${classId}/schedule/auto-fill/`, preferences);
  return { classId, schedules: res.data };
});

// Export schedule to PDF/Excel
export const exportSchedule = createAsyncThunk('schedule/exportSchedule', async ({ classId, format = 'pdf', options = {} }) => {
  const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/students/classes/${classId}/schedule/export/`, 
    { format, ...options }, 
    { responseType: 'blob' }
  );
  return { classId, format, blob: res.data };
});

// Copy schedule from another class
export const copyScheduleFromClass = createAsyncThunk('schedule/copyScheduleFromClass', async ({ fromClassId, toClassId, options = {} }) => {
  const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/students/classes/${toClassId}/schedule/copy-from/${fromClassId}/`, options);
  return { toClassId, schedules: res.data };
});

// Get schedule templates
export const fetchScheduleTemplates = createAsyncThunk('schedule/fetchScheduleTemplates', async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.grade_level) queryParams.append('grade_level', params.grade_level);
  if (params.curriculum) queryParams.append('curriculum', params.curriculum);
  if (params.category) queryParams.append('category', params.category);
  
  const url = `${process.env.REACT_APP_API_BASE_URL}/api/students/schedule-templates/${queryParams.toString() ? `?${queryParams}` : ''}`;
  const res = await axios.get(url);
  return res.data;
});

// Apply schedule template
export const applyScheduleTemplate = createAsyncThunk('schedule/applyScheduleTemplate', async ({ classId, templateId, customizations = {} }) => {
  const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/students/classes/${classId}/schedule/apply-template/${templateId}/`, customizations);
  return { classId, schedules: res.data };
});

// Validate schedule integrity
export const validateSchedule = createAsyncThunk('schedule/validateSchedule', async (classId) => {
  const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/students/classes/${classId}/schedule/validate/`);
  return { classId, validation: res.data };
});

// Get schedule optimization suggestions
export const getOptimizationSuggestions = createAsyncThunk('schedule/getOptimizationSuggestions', async (classId) => {
  const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/students/classes/${classId}/schedule/optimize/`);
  return { classId, suggestions: res.data };
});

// ===== Initial State =====
const initialState = {
  // Schedule data
  schedules: [],
  enhancedSchedules: [],
  classSchedules: {}, // Organized by class ID
  teacherSchedules: {}, // Organized by teacher ID
  currentSchedule: null, // For single schedule details
  
  // Draft schedules
  draftSchedules: [],
  
  // Period timings
  periodTimings: [],
  currentPeriodTiming: null,
  
  // Schedule configurations
  scheduleConfigurations: [],
  
  // Analytics and recommendations
  classAnalytics: {}, // Organized by class ID
  classRecommendations: {}, // Organized by class ID
  
  // Conflicts
  conflicts: {
    conflicts: [],
    total_conflicts: 0,
    by_type: {}
  },
  
  // Loading states
  loading: {
    schedules: false,
    enhancedSchedules: false,
    classSchedule: false,
    teacherSchedule: false,
    scheduleDetail: false,
    conflicts: false,
    periodTimings: false,
    periodTimingDetail: false,
    scheduleConfigurations: false,
    analytics: false,
    recommendations: false,
    creating: false,
    updating: false,
    deleting: false,
    bulkCreating: false,
    draftSchedules: false,
    bulkCreatingDrafts: false,
    publishingDrafts: false
  },
  
  // Error handling
  error: null,
  
  // Filters and sorting
  filters: {
    school_class: null,
    day_of_week: null,
    slot_id: null,
    slot_type: null,
    teacher: null,
    subject: null
  },
  
  // Constants for UI
  constants: {
    daysOfWeek: [
      { value: 'sunday', label: 'الأحد' },
      { value: 'monday', label: 'الاثنين' },
      { value: 'tuesday', label: 'الثلاثاء' },
      { value: 'wednesday', label: 'الأربعاء' },
      { value: 'thursday', label: 'الخميس' },
      { value: 'friday', label: 'الجمعة' },
      { value: 'saturday', label: 'السبت' }
    ],
    slotTypes: [
      { value: 'class', label: 'حصة دراسية' },
      { value: 'break', label: 'استراحة' },
      { value: 'lunch', label: 'استراحة الغداء' },
      { value: 'assembly', label: 'طابور الصباح' },
      { value: 'activity', label: 'نشاط' }
    ],
    periods: [
      { value: 'period_1', label: 'الحصة الأولى' },
      { value: 'period_2', label: 'الحصة الثانية' },
      { value: 'period_3', label: 'الحصة الثالثة' },
      { value: 'period_4', label: 'الحصة الرابعة' },
      { value: 'period_5', label: 'الحصة الخامسة' },
      { value: 'period_6', label: 'الحصة السادسة' },
      { value: 'period_7', label: 'الحصة السابعة' },
      { value: 'period_8', label: 'الحصة الثامنة' },
      { value: 'period_9', label: 'الحصة التاسعة' },
      { value: 'period_10', label: 'الحصة العاشرة' }
    ]
  }
};

// ===== Schedule Slice =====
const scheduleSlice = createSlice({
  name: 'schedule',
  initialState,
  reducers: {
    // Clear errors
    clearError: (state) => {
      state.error = null;
    },
    
    // Set filters
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    
    // Clear filters
    clearFilters: (state) => {
      state.filters = {
        school_class: null,
        day_of_week: null,
        slot_id: null,
        slot_type: null,
        teacher: null,
        subject: null
      };
    },
    
    // Set current schedule
    setCurrentSchedule: (state, action) => {
      state.currentSchedule = action.payload;
    },
    
    // Clear current schedule
    clearCurrentSchedule: (state) => {
      state.currentSchedule = null;
    },
    
    // Set current period timing
    setCurrentPeriodTiming: (state, action) => {
      state.currentPeriodTiming = action.payload;
    },
    
    // Clear current period timing
    clearCurrentPeriodTiming: (state) => {
      state.currentPeriodTiming = null;
    },
    
    // Clear class schedule
    clearClassSchedule: (state, action) => {
      delete state.classSchedules[action.payload];
    },
    
    // Clear teacher schedule
    clearTeacherSchedule: (state, action) => {
      delete state.teacherSchedules[action.payload];
    },
    
    // Clear class analytics
    clearClassAnalytics: (state, action) => {
      delete state.classAnalytics[action.payload];
    },
    
    // Clear class recommendations
    clearClassRecommendations: (state, action) => {
      delete state.classRecommendations[action.payload];
    },
    
    // Reset all schedules
    resetSchedules: (state) => {
      state.schedules = [];
      state.enhancedSchedules = [];
      state.classSchedules = {};
      state.teacherSchedules = {};
      state.currentSchedule = null;
    }
  },
  
  extraReducers: (builder) => {
    builder
      // ===== Fetch All Schedules =====
      .addCase(fetchSchedules.pending, (state) => {
        state.loading.schedules = true;
        state.error = null;
      })
      .addCase(fetchSchedules.fulfilled, (state, action) => {
        state.schedules = action.payload;
        state.loading.schedules = false;
      })
      .addCase(fetchSchedules.rejected, (state, action) => {
        state.loading.schedules = false;
        state.error = action.payload || action.error.message;
      })
      
      // ===== Fetch Schedules By Subject =====
      .addCase(fetchSchedulesBySubject.pending, (state) => {
        state.loading.schedules = true;
        state.error = null;
      })
      .addCase(fetchSchedulesBySubject.fulfilled, (state, action) => {
        state.schedules = action.payload;
        state.loading.schedules = false;
      })
      .addCase(fetchSchedulesBySubject.rejected, (state, action) => {
        state.loading.schedules = false;
        state.error = action.payload || action.error.message;
      })
      
      // ===== Fetch Enhanced Schedules =====
      .addCase(fetchEnhancedSchedules.pending, (state) => {
        state.loading.enhancedSchedules = true;
        state.error = null;
      })
      .addCase(fetchEnhancedSchedules.fulfilled, (state, action) => {
        state.enhancedSchedules = action.payload;
        state.loading.enhancedSchedules = false;
      })
      .addCase(fetchEnhancedSchedules.rejected, (state, action) => {
        state.loading.enhancedSchedules = false;
        state.error = action.payload || action.error.message;
      })
      
      // ===== Fetch Schedule by ID =====
      .addCase(fetchScheduleById.pending, (state) => {
        state.loading.scheduleDetail = true;
        state.error = null;
      })
      .addCase(fetchScheduleById.fulfilled, (state, action) => {
        state.currentSchedule = action.payload;
        state.loading.scheduleDetail = false;
      })
      .addCase(fetchScheduleById.rejected, (state, action) => {
        state.loading.scheduleDetail = false;
        state.error = action.payload || action.error.message;
      })
      
      // ===== Fetch Class Schedule =====
      .addCase(fetchClassSchedule.pending, (state) => {
        state.loading.classSchedule = true;
        state.error = null;
      })
      .addCase(fetchClassSchedule.fulfilled, (state, action) => {
        const { classId, schedules } = action.payload;
        state.classSchedules[classId] = schedules;
        
        // Also update global schedules array - remove old schedules for this class and add new ones
        state.schedules = state.schedules.filter(s => s.school_class !== classId);
        state.schedules.push(...schedules);
        
        state.loading.classSchedule = false;
      })
      .addCase(fetchClassSchedule.rejected, (state, action) => {
  state.loading.classSchedule = false;
  state.error = action.payload || action.error.message;
})
      
      // ===== Fetch Teacher Schedule =====
      .addCase(fetchTeacherSchedule.pending, (state) => {
        state.loading.teacherSchedule = true;
        state.error = null;
      })
      .addCase(fetchTeacherSchedule.fulfilled, (state, action) => {
        const { teacherId, schedules } = action.payload;
        state.teacherSchedules[teacherId] = schedules;
        state.loading.teacherSchedule = false;
      })
      .addCase(fetchTeacherSchedule.rejected, (state, action) => {
        state.loading.teacherSchedule = false;
        state.error = action.payload || action.error.message;
      })
      
      // ===== Create Schedule =====
      .addCase(createSchedule.pending, (state) => {
        state.loading.creating = true;
        state.error = null;
      })
      .addCase(createSchedule.fulfilled, (state, action) => {
        const newSchedule = action.payload;
        
        // Add to main schedules array
        state.schedules.push(newSchedule);
        
        // Add to class schedule if it exists
        if (state.classSchedules[newSchedule.school_class]) {
          state.classSchedules[newSchedule.school_class].push(newSchedule);
        }
        
        // Add to teacher schedule if it exists
        if (newSchedule.teacher && state.teacherSchedules[newSchedule.teacher]) {
          state.teacherSchedules[newSchedule.teacher].push(newSchedule);
        }
        
        state.loading.creating = false;
      })
      .addCase(createSchedule.rejected, (state, action) => {
  state.loading.creating = false;
  state.error = action.payload || action.error.message;
})
      
      // ===== Bulk Create Schedules =====
      .addCase(bulkCreateSchedules.pending, (state) => {
        state.loading.bulkCreating = true;
        state.error = null;
      })
      .addCase(bulkCreateSchedules.fulfilled, (state, action) => {
        const result = action.payload;
        const newSchedules = Array.isArray(result.schedules) ? result.schedules : [];
        
        // Get class ID from schedules or meta
        let classId = null;
        if (newSchedules.length > 0) {
          classId = newSchedules[0].school_class || newSchedules[0].school_class_id;
        } else if (action.meta?.arg?.school_class_id) {
          classId = action.meta.arg.school_class_id;
        }
        
        if (classId) {
          // Remove old schedules for this class from main schedules array
          state.schedules = state.schedules.filter(s => s.school_class !== classId);
          
          // Replace class schedules completely
          state.classSchedules[classId] = [...newSchedules];
          
          // Add new schedules to main schedules array
          state.schedules.push(...newSchedules);
          
          // Update teacher schedules - remove old schedules for this class and add new ones
          // First, collect all teacher IDs from new schedules
          const teacherIds = new Set(newSchedules.map(s => s.teacher).filter(Boolean));
          
          // Remove old schedules for this class from all teacher schedules
          Object.keys(state.teacherSchedules).forEach(teacherId => {
            state.teacherSchedules[teacherId] = state.teacherSchedules[teacherId].filter(
              s => s.school_class !== classId
            );
          });
          
          // Add new schedules to teacher schedules
          newSchedules.forEach(schedule => {
            if (schedule.teacher) {
              if (!state.teacherSchedules[schedule.teacher]) {
                state.teacherSchedules[schedule.teacher] = [];
              }
              state.teacherSchedules[schedule.teacher].push(schedule);
            }
          });
        }
        
        state.loading.bulkCreating = false;
      })
      .addCase(bulkCreateSchedules.rejected, (state, action) => {
  state.loading.bulkCreating = false;
  
  // Store error safely as string to prevent React rendering issues
  const errorData = action.payload || action.error;
  state.error = typeof errorData === 'object' ? JSON.stringify(errorData) : String(errorData);
  
  console.log('🔴 Redux storing error safely:', state.error);
})
      
      // ===== Update Schedule =====
      .addCase(updateSchedule.pending, (state) => {
        state.loading.updating = true;
        state.error = null;
      })
      .addCase(updateSchedule.fulfilled, (state, action) => {
        const updatedSchedule = action.payload;
        
        // Update in main schedules array
        const scheduleIndex = state.schedules.findIndex(s => s.id === updatedSchedule.id);
        if (scheduleIndex !== -1) {
          state.schedules[scheduleIndex] = updatedSchedule;
        }
        
        // Update current schedule if it's the same
        if (state.currentSchedule && state.currentSchedule.id === updatedSchedule.id) {
          state.currentSchedule = updatedSchedule;
        }
        
        // Update in class schedules
        Object.keys(state.classSchedules).forEach(classId => {
          const classIndex = state.classSchedules[classId].findIndex(s => s.id === updatedSchedule.id);
          if (classIndex !== -1) {
            state.classSchedules[classId][classIndex] = updatedSchedule;
          }
        });
        
        // Update in teacher schedules
        Object.keys(state.teacherSchedules).forEach(teacherId => {
          const teacherIndex = state.teacherSchedules[teacherId].findIndex(s => s.id === updatedSchedule.id);
          if (teacherIndex !== -1) {
            state.teacherSchedules[teacherId][teacherIndex] = updatedSchedule;
          }
        });
        
        state.loading.updating = false;
      })
      .addCase(updateSchedule.rejected, (state, action) => {
  state.loading.updating = false;
  state.error = action.payload || action.error.message;
})
      
      // ===== Delete Schedule =====
      .addCase(deleteSchedule.pending, (state) => {
        state.loading.deleting = true;
        state.error = null;
      })
      .addCase(deleteSchedule.fulfilled, (state, action) => {
        const deletedId = action.payload;
        
        // Remove from main schedules array
        state.schedules = state.schedules.filter(s => s.id !== deletedId);
        
        // Clear current schedule if it's the deleted one
        if (state.currentSchedule && state.currentSchedule.id === deletedId) {
          state.currentSchedule = null;
        }
        
        // Remove from class schedules
        Object.keys(state.classSchedules).forEach(classId => {
          state.classSchedules[classId] = state.classSchedules[classId].filter(s => s.id !== deletedId);
        });
        
        // Remove from teacher schedules
        Object.keys(state.teacherSchedules).forEach(teacherId => {
          state.teacherSchedules[teacherId] = state.teacherSchedules[teacherId].filter(s => s.id !== deletedId);
        });
        
        state.loading.deleting = false;
      })
      .addCase(deleteSchedule.rejected, (state, action) => {
        state.loading.deleting = false;
        state.error = action.payload || action.error.message;
      })
      
      // ===== Check Conflicts =====
      .addCase(checkScheduleConflicts.pending, (state) => {
        state.loading.conflicts = true;
        state.error = null;
      })
      .addCase(checkScheduleConflicts.fulfilled, (state, action) => {
        state.conflicts = action.payload;
        state.loading.conflicts = false;
      })
      .addCase(checkScheduleConflicts.rejected, (state, action) => {
        state.loading.conflicts = false;
        state.error = action.payload || action.error.message;
      })
      
      // ===== Schedule Analytics =====
      .addCase(fetchScheduleAnalytics.pending, (state) => {
        state.loading.analytics = true;
        state.error = null;
      })
      .addCase(fetchScheduleAnalytics.fulfilled, (state, action) => {
        const { classId, analytics } = action.payload;
        state.classAnalytics[classId] = analytics;
        state.loading.analytics = false;
      })
      .addCase(fetchScheduleAnalytics.rejected, (state, action) => {
        state.loading.analytics = false;
        state.error = action.payload || action.error.message;
      })
      
      // ===== Schedule Recommendations =====
      .addCase(fetchScheduleRecommendations.pending, (state) => {
        state.loading.recommendations = true;
        state.error = null;
      })
      .addCase(fetchScheduleRecommendations.fulfilled, (state, action) => {
        const { classId, recommendations } = action.payload;
        state.classRecommendations[classId] = recommendations;
        state.loading.recommendations = false;
      })
      .addCase(fetchScheduleRecommendations.rejected, (state, action) => {
        state.loading.recommendations = false;
        state.error = action.payload || action.error.message;
      })
      
      // ===== Period Timings =====
      .addCase(fetchPeriodTimings.pending, (state) => {
        state.loading.periodTimings = true;
        state.error = null;
      })
      .addCase(fetchPeriodTimings.fulfilled, (state, action) => {
        state.periodTimings = action.payload;
        state.loading.periodTimings = false;
      })
      .addCase(fetchPeriodTimings.rejected, (state, action) => {
        state.loading.periodTimings = false;
        state.error = action.payload || action.error.message;
      })
      
      // Fetch Period Timing by ID
      .addCase(fetchPeriodTimingById.pending, (state) => {
        state.loading.periodTimingDetail = true;
        state.error = null;
      })
      .addCase(fetchPeriodTimingById.fulfilled, (state, action) => {
        state.currentPeriodTiming = action.payload;
        state.loading.periodTimingDetail = false;
      })
      .addCase(fetchPeriodTimingById.rejected, (state, action) => {
        state.loading.periodTimingDetail = false;
        state.error = action.payload || action.error.message;
      })
      
      // Create Period Timing
      .addCase(createPeriodTiming.fulfilled, (state, action) => {
        state.periodTimings.push(action.payload);
      })
      
      // Update Period Timing
      .addCase(updatePeriodTiming.fulfilled, (state, action) => {
        const index = state.periodTimings.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.periodTimings[index] = action.payload;
        }
        
        // Update current period timing if it's the same
        if (state.currentPeriodTiming && state.currentPeriodTiming.id === action.payload.id) {
          state.currentPeriodTiming = action.payload;
        }
      })
      
      // Delete Period Timing
      .addCase(deletePeriodTiming.fulfilled, (state, action) => {
        state.periodTimings = state.periodTimings.filter(t => t.id !== action.payload);
        
        // Clear current period timing if it's the deleted one
        if (state.currentPeriodTiming && state.currentPeriodTiming.id === action.payload) {
          state.currentPeriodTiming = null;
        }
      })
      
      // ===== Schedule Configurations =====
      .addCase(fetchScheduleConfigurations.pending, (state) => {
        state.loading.scheduleConfigurations = true;
        state.error = null;
      })
      .addCase(fetchScheduleConfigurations.fulfilled, (state, action) => {
        state.scheduleConfigurations = action.payload;
        state.loading.scheduleConfigurations = false;
      })
      .addCase(fetchScheduleConfigurations.rejected, (state, action) => {
        state.loading.scheduleConfigurations = false;
        state.error = action.payload || action.error.message;
      })
      
      // Create Schedule Configuration
      .addCase(createScheduleConfiguration.fulfilled, (state, action) => {
        state.scheduleConfigurations.push(action.payload);
      })
      
      // Update Schedule Configuration
      .addCase(updateScheduleConfiguration.fulfilled, (state, action) => {
        const index = state.scheduleConfigurations.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.scheduleConfigurations[index] = action.payload;
        }
      })
      
      // Delete Schedule Configuration
      .addCase(deleteScheduleConfiguration.fulfilled, (state, action) => {
        state.scheduleConfigurations = state.scheduleConfigurations.filter(c => c.id !== action.payload);
      })
      
      // ===== Draft Schedules =====
      .addCase(fetchDraftSchedules.pending, (state) => {
        state.loading.draftSchedules = true;
        state.error = null;
      })
      .addCase(fetchDraftSchedules.fulfilled, (state, action) => {
        // Ensure payload is an array
        const drafts = Array.isArray(action.payload) ? action.payload : [];
        console.log('📦 Storing drafts in Redux:', drafts.length, drafts);
        state.draftSchedules = drafts;
        state.loading.draftSchedules = false;
      })
      .addCase(fetchDraftSchedules.rejected, (state, action) => {
        state.loading.draftSchedules = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(bulkCreateDraftSchedules.pending, (state) => {
        state.loading.bulkCreatingDrafts = true;
        state.error = null;
      })
      .addCase(bulkCreateDraftSchedules.fulfilled, (state, action) => {
        const result = action.payload;
        const newDrafts = Array.isArray(result.drafts) ? result.drafts : (result.data?.drafts || []);
        
        // Replace drafts for the specific class
        const classId = newDrafts[0]?.school_class || newDrafts[0]?.school_class_id;
        if (classId) {
          // Remove old drafts for this class
          state.draftSchedules = state.draftSchedules.filter(d => d.school_class !== classId);
          // Add new drafts
          state.draftSchedules.push(...newDrafts);
        } else {
          state.draftSchedules.push(...newDrafts);
        }
        state.loading.bulkCreatingDrafts = false;
      })
      .addCase(bulkCreateDraftSchedules.rejected, (state, action) => {
        state.loading.bulkCreatingDrafts = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(deleteDraftSchedule.fulfilled, (state, action) => {
        state.draftSchedules = state.draftSchedules.filter(d => d.id !== action.payload);
      })
      .addCase(publishDraftSchedules.pending, (state) => {
        state.loading.publishingDrafts = true;
        state.error = null;
      })
      .addCase(publishDraftSchedules.fulfilled, (state, action) => {
        // Remove published drafts
        const schoolClassId = action.meta?.arg?.school_class_id;
        if (schoolClassId) {
          state.draftSchedules = state.draftSchedules.filter(d => d.school_class !== schoolClassId);
        } else {
          // If no class specified, remove all drafts (shouldn't happen but safe)
          state.draftSchedules = [];
        }
        state.loading.publishingDrafts = false;
        // Refresh schedules after publishing
        // Note: The component should call fetchSchedules after publishing
      })
      .addCase(publishDraftSchedules.rejected, (state, action) => {
        state.loading.publishingDrafts = false;
        state.error = action.payload || action.error.message;
      });
  }
});

// ===== Export Actions and Selectors =====
export const {
  clearError,
  setFilters,
  clearFilters,
  setCurrentSchedule,
  clearCurrentSchedule,
  setCurrentPeriodTiming,
  clearCurrentPeriodTiming,
  clearClassSchedule,
  clearTeacherSchedule,
  clearClassAnalytics,
  clearClassRecommendations,
  resetSchedules
} = scheduleSlice.actions;

// ===== Selectors =====
export const selectSchedules = (state) => state.schedule?.schedules || [];
export const selectEnhancedSchedules = (state) => state.schedule?.enhancedSchedules || [];
export const selectCurrentSchedule = (state) => state.schedule?.currentSchedule || null;
export const selectClassSchedule = (state, classId) => state.schedule?.classSchedules?.[classId] || [];
export const selectTeacherSchedule = (state, teacherId) => state.schedule?.teacherSchedules?.[teacherId] || [];
export const selectPeriodTimings = (state) => state.schedule?.periodTimings || [];
export const selectCurrentPeriodTiming = (state) => state.schedule?.currentPeriodTiming || null;
export const selectScheduleConfigurations = (state) => state.schedule?.scheduleConfigurations || [];
export const selectClassAnalytics = (state, classId) => state.schedule?.classAnalytics?.[classId] || null;
export const selectClassRecommendations = (state, classId) => state.schedule?.classRecommendations?.[classId] || null;
export const selectConflicts = (state) => state.schedule?.conflicts || { conflicts: [], total_conflicts: 0, by_type: {} };
export const selectLoading = (state) => state.schedule?.loading || {};
export const selectError = (state) => state.schedule?.error || null;
export const selectFilters = (state) => state.schedule?.filters || {};
export const selectConstants = (state) => state.schedule?.constants || { daysOfWeek: [], slotTypes: [], periods: [] };

// Complex selectors
export const selectSchedulesByDay = (state, dayOfWeek) => 
  (state.schedule?.schedules || []).filter(schedule => schedule.day_of_week === dayOfWeek);

export const selectSchedulesByClass = (state, classId) => 
  (state.schedule?.schedules || []).filter(schedule => schedule.school_class === classId);

export const selectSchedulesByTeacher = (state, teacherId) => 
  (state.schedule?.schedules || []).filter(schedule => schedule.teacher === teacherId);

export const selectSchedulesBySlotType = (state, slotType) => 
  (state.schedule?.schedules || []).filter(schedule => schedule.slot_type === slotType);

export const selectWeeklyScheduleGrid = (state, classId = null) => {
  const schedules = classId 
    ? selectSchedulesByClass(state, classId)
    : (state.schedule?.schedules || []);
  
  const grid = {};
  const daysOfWeek = state.schedule?.constants?.daysOfWeek || [];
  const periodTimings = state.schedule?.periodTimings || [];
  const periods = state.schedule?.constants?.periods || [];
  
  daysOfWeek.forEach(day => {
    grid[day.value] = {};
    // Initialize grid with empty slots based on period timings or default periods
    if (periodTimings.length > 0) {
      periodTimings.forEach(timing => {
        grid[day.value][timing.period] = null;
      });
    } else {
      periods.forEach(period => {
        grid[day.value][period.value] = null;
      });
    }
  });
  
  schedules.forEach(schedule => {
    if (grid[schedule.day_of_week] && grid[schedule.day_of_week][schedule.slot_id] !== undefined) {
      grid[schedule.day_of_week][schedule.slot_id] = schedule;
    }
  });
  
  return grid;
};

export const selectEnhancedWeeklyScheduleGrid = (state, classId = null) => {
  const schedules = classId 
    ? (state.schedule?.classSchedules?.[classId] || [])
    : (state.schedule?.enhancedSchedules || []);
  
  const grid = {};
  const daysOfWeek = state.schedule?.constants?.daysOfWeek || [];
  
  daysOfWeek.forEach(day => {
    grid[day.value] = [];
  });
  
  schedules.forEach(schedule => {
    if (grid[schedule.day_of_week]) {
      grid[schedule.day_of_week].push(schedule);
    }
  });
  
  // Sort each day's schedules by start time
  Object.keys(grid).forEach(day => {
    grid[day].sort((a, b) => {
      if (a.start_time && b.start_time) {
        return a.start_time.localeCompare(b.start_time);
      }
      return 0;
    });
  });
  
  return grid;
};

export const selectHasConflicts = (state) => (state.schedule?.conflicts?.total_conflicts || 0) > 0;

export const selectFilteredSchedules = (state) => {
  const schedules = state.schedule?.schedules || [];
  const filters = state.schedule?.filters || {};
  
  return schedules.filter(schedule => {
    if (filters.school_class && schedule.school_class !== filters.school_class) return false;
    if (filters.day_of_week && schedule.day_of_week !== filters.day_of_week) return false;
    if (filters.slot_id && schedule.slot_id !== filters.slot_id) return false;
    if (filters.slot_type && schedule.slot_type !== filters.slot_type) return false;
    if (filters.teacher && schedule.teacher !== filters.teacher) return false;
    if (filters.subject && schedule.subject !== filters.subject) return false;
    return true;
  });
};

export const selectDefaultScheduleConfiguration = (state) => 
  (state.schedule?.scheduleConfigurations || []).find(config => config.is_default) || null;

export const selectActiveScheduleConfigurations = (state) => 
  (state.schedule?.scheduleConfigurations || []).filter(config => !config.is_archived);

export const selectActivePeriodTimings = (state) => 
  (state.schedule?.periodTimings || []).filter(timing => timing.is_active);

export const selectScheduleStatistics = (state, classId = null) => {
  const schedules = classId 
    ? selectSchedulesByClass(state, classId)
    : (state.schedule?.schedules || []);
  
  const stats = {
    total_schedules: schedules.length,
    by_day: {},
    by_slot_type: {},
    by_teacher: {},
    by_subject: {},
    total_hours: 0
  };
  
  const daysOfWeek = state.schedule?.constants?.daysOfWeek || [];
  const slotTypes = state.schedule?.constants?.slotTypes || [];
  
  // Initialize day counts
  daysOfWeek.forEach(day => {
    stats.by_day[day.value] = 0;
  });
  
  // Initialize slot type counts
  slotTypes.forEach(type => {
    stats.by_slot_type[type.value] = 0;
  });
  
  schedules.forEach(schedule => {
    // Count by day
    if (stats.by_day[schedule.day_of_week] !== undefined) {
      stats.by_day[schedule.day_of_week]++;
    }
    
    // Count by slot type
    if (stats.by_slot_type[schedule.slot_type] !== undefined) {
      stats.by_slot_type[schedule.slot_type]++;
    }
    
    // Count by teacher
    if (schedule.teacher) {
      stats.by_teacher[schedule.teacher] = (stats.by_teacher[schedule.teacher] || 0) + 1;
    }
    
    // Count by subject
    if (schedule.subject) {
      stats.by_subject[schedule.subject] = (stats.by_subject[schedule.subject] || 0) + 1;
    }
    
    // Calculate total hours
    if (schedule.duration_minutes) {
      stats.total_hours += schedule.duration_minutes / 60;
    }
  });
  
  return stats;
};

export const selectTeacherWorkload = (state, teacherId) => {
  const teacherSchedules = selectSchedulesByTeacher(state, teacherId);
  
  const workload = {
    total_classes: teacherSchedules.filter(s => s.slot_type === 'class').length,
    total_hours: 0,
    daily_schedule: {},
    subjects: {}
  };
  
  const daysOfWeek = state.schedule?.constants?.daysOfWeek || [];
  
  // Initialize daily schedule
  daysOfWeek.forEach(day => {
    workload.daily_schedule[day.value] = {
      classes: 0,
      hours: 0,
      schedules: []
    };
  });
  
  teacherSchedules.forEach(schedule => {
    // Add to daily schedule
    if (workload.daily_schedule[schedule.day_of_week]) {
      workload.daily_schedule[schedule.day_of_week].schedules.push(schedule);
      if (schedule.slot_type === 'class') {
        workload.daily_schedule[schedule.day_of_week].classes++;
      }
      if (schedule.duration_minutes) {
        const hours = schedule.duration_minutes / 60;
        workload.daily_schedule[schedule.day_of_week].hours += hours;
        workload.total_hours += hours;
      }
    }
    
    // Count subjects
    if (schedule.subject && schedule.slot_type === 'class') {
      workload.subjects[schedule.subject] = (workload.subjects[schedule.subject] || 0) + 1;
    }
  });
  
  return workload;
};

export const selectConflictsByType = (state) => {
  const conflicts = state.schedule?.conflicts?.conflicts || [];
  const byType = {};
  
  conflicts.forEach(conflict => {
    const type = conflict.conflict_type || 'general';
    if (!byType[type]) {
      byType[type] = [];
    }
    byType[type].push(conflict);
  });
  
  return byType;
};

export const selectUpcomingSchedules = (state, classId = null, limit = 5) => {
  const now = new Date();
  const currentTime = now.toTimeString().slice(0, 5); // HH:MM format
  
  const schedules = classId 
    ? selectSchedulesByClass(state, classId)
    : (state.schedule?.schedules || []);
  
  return schedules
    .filter(schedule => {
      // Filter for today's upcoming schedules or future days
      if (schedule.day_of_week === getDayName(now.getDay())) {
        return schedule.start_time > currentTime;
      }
      return true; // Include other days
    })
    .sort((a, b) => {
      // Sort by day of week, then by start time
      const dayOrder = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const dayA = dayOrder.indexOf(a.day_of_week);
      const dayB = dayOrder.indexOf(b.day_of_week);
      
      if (dayA !== dayB) {
        return dayA - dayB;
      }
      
      return a.start_time.localeCompare(b.start_time);
    })
    .slice(0, limit);
};

// Helper function to get day name from day number
const getDayName = (dayNumber) => {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[dayNumber];
};

export const selectSchedulesByTimeRange = (state, startTime, endTime, dayOfWeek = null) => {
  let schedules = state.schedule?.schedules || [];
  
  if (dayOfWeek) {
    schedules = schedules.filter(s => s.day_of_week === dayOfWeek);
  }
  
  return schedules.filter(schedule => {
    if (!schedule.start_time || !schedule.end_time) return false;
    
    return (
      schedule.start_time >= startTime && 
      schedule.end_time <= endTime
    ) || (
      schedule.start_time <= startTime && 
      schedule.end_time >= endTime
    ) || (
      schedule.start_time <= startTime && 
      schedule.end_time > startTime
    ) || (
      schedule.start_time < endTime && 
      schedule.end_time >= endTime
    );
  });
};

export const selectFreeTimeSlots = (state, classId, dayOfWeek) => {
  const classSchedules = selectSchedulesByClass(state, classId)
    .filter(s => s.day_of_week === dayOfWeek)
    .sort((a, b) => a.start_time.localeCompare(b.start_time));
  
  const periodTimings = selectActivePeriodTimings(state);
  const freeSlots = [];
  
  if (periodTimings.length === 0) return freeSlots;
  
  // Find gaps between scheduled classes
  for (let i = 0; i < periodTimings.length; i++) {
    const period = periodTimings[i];
    const hasSchedule = classSchedules.some(s => 
      s.start_time <= period.start_time && s.end_time >= period.end_time
    );
    
    if (!hasSchedule) {
      freeSlots.push({
        period: period.period,
        start_time: period.start_time,
        end_time: period.end_time,
        duration_minutes: calculateDuration(period.start_time, period.end_time)
      });
    }
  }
  
  return freeSlots;
};

// Helper function to calculate duration in minutes
const calculateDuration = (startTime, endTime) => {
  const start = new Date(`2000-01-01T${startTime}:00`);
  const end = new Date(`2000-01-01T${endTime}:00`);
  return (end - start) / (1000 * 60);
};

export default scheduleSlice.reducer;